import { Checkbox, ListItem, ListItemIcon, ListItemText } from "~/common/mui";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import type { Identifier } from "dnd-core";

export interface ColumnData {
  code: string;
  visible: boolean;
  label: string;
}

interface ColumnConfiguratorProps {
  items: ColumnData[];
  moveColumn: (oldIndex: number, newIndex: number) => void;
  updateVisibility: (index: number, visible: boolean) => void;
}

type DragObject = { id: string; index: number };

export const ColumnConfigurator = ({ items, moveColumn, updateVisibility }: ColumnConfiguratorProps) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        {items.map((item, i) => (
          <ColumnItem
            {...item}
            index={i}
            key={item.code}
            moveColumn={moveColumn}
            updateVisibility={(visible) => updateVisibility(i, visible)}
          />
        ))}
      </div>
    </DndProvider>
  );
};

interface ColumnItemProps {
  code: string;
  visible: boolean;
  label: string;
  index: number;
  moveColumn: (oldIndex: number, newIndex: number) => void;
  updateVisibility: (visible: boolean) => void;
}

const ColumnItem = ({ code, visible, label, index, moveColumn, updateVisibility }: ColumnItemProps) => {
  const ref = useRef<HTMLLIElement>(null);
  const [{ handlerId }, drop] = useDrop<DragObject, unknown, { handlerId: Identifier | null }>({
    accept: "item",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()!;
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveColumn(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });
  const [{ opacity }, drag, preview] = useDrag<DragObject, unknown, { opacity: number }>({
    type: "item",
    item: () => {
      return { id: code, index };
    },
    collect: (monitor) => ({
      opacity: monitor.isDragging() ? 0 : 1,
    }),
  });
  drag(drop(ref));

  return (
    <ListItem
      ref={preview}
      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      style={{ opacity }}
      density={-4}
    >
      <ListItemIcon ref={ref} sx={{ cursor: "pointer" }}>
        <DragIndicatorIcon />
      </ListItemIcon>
      <ListItemText>{label}</ListItemText>
      <Checkbox
        checked={visible}
        onChange={(e, value) => {
          updateVisibility(value);
        }}
      />
    </ListItem>
  );
};
